import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import ClientShell from "@/components/ClientShell";
import { Lightning, PaperPlaneTilt, UploadSimple, X, FileText, Image as ImageIcon, ArrowLeft } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const ALLOWED = [".pdf", ".jpg", ".jpeg", ".png", ".dwg", ".dxf"];
const MAX_FILES = 5;

export default function QuickInquiry() {
  const navigate = useNavigate();
  const [requirement, setRequirement] = useState("");
  const [dimensions, setDimensions] = useState("");
  const [projectName, setProjectName] = useState("");
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [uploadingName, setUploadingName] = useState("");
  const [uploadPct, setUploadPct] = useState(0);
  const fileRef = useRef(null);

  const isImage = (f) => /\.(jpg|jpeg|png)$/i.test(f.name);

  const onPickFiles = (e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = "";
    const next = [...files];
    for (const f of picked) {
      const ext = "." + (f.name.split(".").pop() || "").toLowerCase();
      if (!ALLOWED.includes(ext)) {
        toast.error(`${f.name}: unsupported type. Use PDF, JPG, PNG, DWG or DXF.`);
        continue;
      }
      if (next.length >= MAX_FILES) {
        toast.error(`You can attach up to ${MAX_FILES} files.`);
        break;
      }
      if (!next.some((x) => x.name === f.name && x.size === f.size)) next.push(f);
    }
    setFiles(next);
  };

  const removeFile = (idx) => setFiles((fs) => fs.filter((_, i) => i !== idx));

  const submit = async (e) => {
    e.preventDefault();
    if (!requirement.trim()) {
      toast.error("Please tell us what you need.");
      return;
    }
    setBusy(true);
    try {
      const res = await api.post("/quotations/quick", {
        requirement: requirement.trim(),
        dimensions: dimensions.trim(),
        project_name: projectName.trim(),
      });
      const quotationId = res.data.quotation_id;
      if (files.length > 0) {
        for (let i = 0; i < files.length; i += 1) {
          const f = files[i];
          setUploadingName(f.name);
          const fd = new FormData();
          fd.append("file", f);
          try {
            await api.post(`/quotations/${quotationId}/attachments`, fd, {
              headers: { "Content-Type": "multipart/form-data" },
              onUploadProgress: (evt) => {
                const filePct = evt.total ? evt.loaded / evt.total : 0;
                setUploadPct(Math.round(((i + filePct) / files.length) * 100));
              },
            });
          } catch (upErr) {
            const d = upErr?.response?.data?.detail;
            toast.error(typeof d === "string" ? `${f.name}: ${d}` : `Couldn't upload ${f.name}`);
          }
        }
      }
      toast.success("Inquiry sent — our team will get back to you with a quote shortly.");
      navigate("/quotations");
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not submit your inquiry.");
    } finally {
      setBusy(false);
      setUploadingName("");
      setUploadPct(0);
    }
  };

  return (
    <ClientShell>
      <div className="max-w-2xl mx-auto pt-6 md:pt-8 pb-16">
        <button
          type="button"
          data-testid="quick-inquiry-back"
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 text-sm text-zinc-500 hover:text-zinc-900 transition-colors mb-5"
        >
          <ArrowLeft size={18} /> Back
        </button>

        <div className="mt-tiny text-[#FF5A1F]">Fast quote request</div>
        <h1 className="font-display text-3xl md:text-4xl font-black tracking-tight text-zinc-900 mt-2">
          Quick Inquiry
        </h1>

        <div className="mt-5 flex items-start gap-3 bg-[#FFF3EC] border border-[#FFD9C2] rounded-xl p-4">
          <Lightning size={20} weight="fill" className="text-[#FF5A1F] shrink-0 mt-0.5" />
          <p className="text-sm text-zinc-700 leading-relaxed">
            On site? Tell us what you need and attach a photo or drawing — we&apos;ll send a quote.
          </p>
        </div>

        <form onSubmit={submit} className="mt-6 space-y-5">
          <div>
            <label className="block text-xs uppercase tracking-wide text-zinc-500 mb-2">What do you need? *</label>
            <Textarea
              data-testid="qi-requirement-input"
              value={requirement}
              onChange={(e) => setRequirement(e.target.value)}
              placeholder="e.g. Marble slabs for a kitchen countertop"
              rows={3}
              className="border-zinc-200"
            />
          </div>

          <div>
            <label className="block text-xs uppercase tracking-wide text-zinc-500 mb-2">Dimensions / specs</label>
            <Input
              data-testid="qi-dimensions-input"
              value={dimensions}
              onChange={(e) => setDimensions(e.target.value)}
              placeholder="e.g. 2400 x 650 mm, 20 mm thick"
              className="border-zinc-200"
            />
          </div>

          <div>
            <label className="block text-xs uppercase tracking-wide text-zinc-500 mb-2">Project name</label>
            <Input
              data-testid="qi-project-input"
              value={projectName}
              onChange={(e) => setProjectName(e.target.value)}
              placeholder="e.g. Villa 88 (optional)"
              className="border-zinc-200"
            />
          </div>

          <div>
            <label className="block text-xs uppercase tracking-wide text-zinc-500 mb-2">Files &amp; photos</label>
            <button
              type="button"
              data-testid="qi-add-files-button"
              onClick={() => fileRef.current?.click()}
              disabled={busy || files.length >= MAX_FILES}
              className="w-full flex flex-col items-center justify-center gap-1 border border-dashed border-zinc-300 bg-white rounded-xl py-6 hover:border-zinc-900 transition-colors disabled:opacity-50"
            >
              <UploadSimple size={22} className="text-[#FF5A1F]" />
              <span className="text-sm font-semibold text-zinc-700">Upload files / photos</span>
              <span className="text-xs text-zinc-400">{files.length}/{MAX_FILES} added · PDF, JPG, PNG, DWG, DXF</span>
            </button>
            <input
              ref={fileRef}
              type="file"
              multiple
              accept=".pdf,.jpg,.jpeg,.png,.dwg,.dxf,image/*,application/pdf"
              onChange={onPickFiles}
              className="hidden"
              data-testid="qi-file-input"
            />

            {files.map((f, idx) => (
              <div
                key={`${f.name}-${idx}`}
                data-testid={`qi-file-${idx}`}
                className="flex items-center gap-3 bg-white border border-zinc-200 rounded-lg p-2.5 mt-2"
              >
                {isImage(f) ? (
                  <ImageIcon size={18} className="text-[#0047AB]" />
                ) : (
                  <FileText size={18} className="text-red-500" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-zinc-900 truncate">{f.name}</div>
                  <div className="text-xs text-zinc-400">{(f.size / 1024 / 1024).toFixed(2)} MB</div>
                </div>
                {!busy && (
                  <button type="button" onClick={() => removeFile(idx)} data-testid={`qi-file-remove-${idx}`}>
                    <X size={18} className="text-zinc-400 hover:text-zinc-900" />
                  </button>
                )}
              </div>
            ))}

            {busy && uploadingName ? (
              <div className="mt-3" data-testid="qi-upload-progress">
                <div className="text-xs text-zinc-500 mb-1.5">Uploading {uploadingName}… {uploadPct}%</div>
                <div className="h-2 bg-zinc-100 rounded-full overflow-hidden">
                  <div className="h-2 bg-[#FF5A1F] rounded-full transition-all" style={{ width: `${uploadPct}%` }} />
                </div>
              </div>
            ) : null}
          </div>

          <button
            type="submit"
            data-testid="qi-submit-button"
            disabled={busy}
            className="w-full flex items-center justify-center gap-2 bg-zinc-900 text-white font-semibold py-3.5 rounded-xl hover:bg-zinc-800 transition-colors disabled:opacity-60"
          >
            <PaperPlaneTilt size={18} weight="fill" />
            {busy ? "Sending…" : "Send inquiry"}
          </button>
        </form>
      </div>
    </ClientShell>
  );
}
